import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SpaceBetween } from "@amzn/awsui-components-react";
import Page from "components/page";
import Help from "pages/conversational-history/Help";
import { useTranslation } from "react-i18next";
import ConversationGroup from "./ConversationGroup";
import { useUserNameSelector } from "pages/team_settings/hooks";
import {
  AtlasChatbotProps,
  GetConversationListRequest,
} from "@amzn/atlas-chat-react-library";
import { IConversationalHistoryProps } from "./definitions";
import { startOfDay, endOfDay, subDays } from "date-fns";
import styles from "./style.module.scss";
import { useBreadcrumbs } from "features/breadcrumbs/hooks";
import { GaiaConstants } from "utils/gaia-constants";

const ConversationalHistory: React.FC<IConversationalHistoryProps> = () => {
  const { userName } = useUserNameSelector();
  const { t } = useTranslation();
  const selectedTeamName =
    localStorage.getItem(GaiaConstants.SELECTED_TEAM_NAME) || "";
  const { updateBreadcrumbs } = useBreadcrumbs();
  const [groupedConversationRequests, setGroupedConversationRequests] =
    React.useState<GetConversationListRequest[]>([]);
  const [groupedConversations, setGroupedConversations] = useState<
    AtlasChatbotProps.GroupedConversation[]
  >([]);
  const [refreshCounters, setRefreshCounters] = useState<Map<number, number>>(
    new Map(),
  );

  const breadcrumbItems = useMemo(
    () => [
      { text: "Gaia", href: "/" },
      { text: t("pages.gaia_ai.chatbot.title"), href: "/gaia-ai/chatbot" },
      { text: t("pages.gaia_ai.conversational_history.title"), href: "#" },
    ],
    [t],
  );

  useEffect(() => {
    updateBreadcrumbs(breadcrumbItems);
  }, [breadcrumbItems, updateBreadcrumbs]);

  useEffect(() => {
    if (!userName) {
      return;
    }

    loadConversationGroups();
  }, [userName]);

  const loadConversationGroups = useCallback(() => {
    if (!userName || selectedTeamName === undefined) {
      return;
    }

    const request: GetConversationListRequest = {
      user: userName,
      appId: selectedTeamName,
    };

    const requests: GetConversationListRequest[] = [
      // today
      {
        ...request,
        startDate: Math.floor(startOfDay(new Date()).getTime() / 1000),
        endDate: Math.floor(endOfDay(new Date()).getTime() / 1000),
      },
      // only yesterday
      {
        ...request,
        startDate: Math.floor(
          startOfDay(subDays(new Date(), 1)).getTime() / 1000,
        ),
        endDate: Math.floor(endOfDay(subDays(new Date(), 1)).getTime() / 1000),
      },
      // the last 7 days minus today and yesterday
      {
        ...request,
        startDate: Math.floor(
          startOfDay(subDays(new Date(), 7)).getTime() / 1000,
        ),
        endDate: Math.floor(endOfDay(subDays(new Date(), 2)).getTime() / 1000),
      },
      // the last 30 days minus the last 7 days
      {
        ...request,
        startDate: Math.floor(
          startOfDay(subDays(new Date(), 30)).getTime() / 1000,
        ),
        endDate: Math.floor(endOfDay(subDays(new Date(), 8)).getTime() / 1000),
      },
    ];

    const groupedConversations: AtlasChatbotProps.GroupedConversation[] = [
      {
        title: t("pages.gaia_ai.conversational_history.today"),
        conversations: [],
      },
      {
        title: t("pages.gaia_ai.conversational_history.yesterday"),
        conversations: [],
      },
      {
        title: t("pages.gaia_ai.conversational_history.lastSevenDays"),
        conversations: [],
      },
      {
        title: t("pages.gaia_ai.conversational_history.lastThirtyDays"),
        conversations: [],
      },
    ];

    setGroupedConversationRequests(requests);
    setGroupedConversations(groupedConversations);
  }, [userName, selectedTeamName]);

  return (
    <Page
      type="page"
      title={t("pages.gaia_ai.conversational_history.title")}
      description={t("pages.gaia_ai.conversational_history.description")}
      hasBreadcrumbs={true}
      hasInfo={true}
      help={<Help />}
      className={styles.conversationalHistory}
    >
      <SpaceBetween size="xl">
        {groupedConversationRequests?.map(
          (request: GetConversationListRequest, index: number) => (
            <ConversationGroup
              key={`conversation-group-${index}`}
              title={groupedConversations[index]?.title || `Group ${index + 1}`}
              conversationListRequest={request}
              onDataUpdated={() => {
                setRefreshCounters(
                  (prev) =>
                    new Map(prev.set(index, (prev.get(index) || 0) + 1)),
                );
              }}
              forceRefresh={refreshCounters.get(index) || 0}
            />
          ),
        )}
      </SpaceBetween>
    </Page>
  );
};

export default ConversationalHistory;
